/**
 * governance/cognitiveLevel.ts
 * Cognitive level detection for questions and explanations.
 *
 * Maps questions to Bloom's taxonomy levels (simplified 4-level model):
 * - recall:        Reproduce facts, definitions, procedures
 * - comprehension: Explain meaning, interpret, paraphrase
 * - application:   Apply formula/rule to a new situation, calculate
 * - analysis:      Compare, contrast, decompose, reason about structure
 * - unknown:       Pattern-detector could not classify
 *
 * FG5 RESTRUCTURE — ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §FG5
 * ==========================================================
 * Pre-FG5 precedence was: author → regex cascade → unknown. That made the
 * REGEX the decisive signal for any non-annotated question, which broke on
 * short, terse DOK-3 questions (e.g. a `step_solver` with no "waarom" text).
 *
 * Post-FG5 precedence (STRUCTURE-FIRST, regex-as-tiebreaker):
 *   1. Explicit author annotation                       (confidence 1.0)
 *   2. STRUCTURAL signals via `cognitiveSignals.ts` SSOT  (confidence 0.8)
 *        - questionType floor (e.g. step_solver ≥ application)
 *        - solutionSteps length (≥ 2 ⇒ application minimum)
 *        - cognitiveCeiling clamp (a type's structural maximum)
 *   3. Regex cascade — ONLY within [floor, ceiling]      (confidence 0.4)
 *   4. unknown                                            (downstream decides)
 *
 * Both `dokAutoTagger` and `cognitiveLevel` are MEANT to consume the same
 * structural facts from `cognitiveSignals.ts`. In practice that is only
 * partially true today (GOVERNANCE_DETANGLE_PLAN_2026 §16.2 + §16.13):
 * `cognitiveLevel` reads `cognitiveSignals.TYPE_FLOOR/TYPE_CEILING`, but
 * `dokAutoTagger` still uses its own `DOK*_TYPES` baseline AND the governance
 * sidecar's per-type `cognitiveCeiling` for clamping. For `multiple_choice`
 * the ceilings diverge (here: no clamp / DOK4; autotagger: DOK2), so DOK and
 * Bloom CAN still drift. Full convergence is behavior-changing → shadow-gated.
 *
 * Anti-bandaid principle #5: a heuristic must never pretend to be truth.
 * Callers needing confidence should use `detectCognitiveLevelDetailed`;
 * the legacy `detectCognitiveLevel` is preserved for existing consumers.
 */

import type { Question } from '../content/types'
import {
  getCognitiveBoundsForType,
  clampToCognitiveBounds,
  type CognitiveTier,
} from '../shared/proxyContracts/cognitiveSignals'

export type CognitiveLevel =
  | 'recall'
  | 'comprehension'
  | 'application'
  | 'analysis'
  | 'unknown'

// ─── Cognitive Level Ranking (GSR-27, audit 2026) ─────────────────────────────

/**
 * Canonical ordinal ranking of cognitive levels — single source of truth.
 * Used by Plus-tier enforcement (assessmentChecks.ts) to compare an
 * auto-detected cogLevel against plusReqs.minCognitiveLevel.
 *
 * NOTE: 'unknown' is intentionally NOT in this map. Consumers must handle
 * 'unknown' explicitly BEFORE looking up the rank — see assessmentChecks.ts
 * where the CONTENT_COGNITIVE_LEVEL_UNKNOWN info() branch fires first.
 */
export const COGNITIVE_LEVEL_RANK: Record<
  Exclude<CognitiveLevel, 'unknown'>,
  number
> = {
  recall: 0,
  comprehension: 1,
  application: 2,
  analysis: 3,
} as const

// ─── Detection patterns (now tiebreakers within structural bounds) ───────────

/**
 * Analysis-level indicators: comparison, reasoning, decomposition,
 * error analysis, reverse reasoning.
 *
 * IMPORTANT — these patterns describe COGNITIVE DEMAND, not question
 * FORMAT. Phrases like "waar of onwaar", "juist of onjuist", or
 * "in de juiste volgorde" describe HOW the learner answers, not WHAT
 * they must reason about. Format patterns were removed mei 2026 after
 * causing false-positive CONTENT_BASIS_COGNITIVE_TOO_HIGH issues on
 * legitimate low-Bloom T/F items.
 */
const ANALYSIS_PATTERNS =
  /vergelijk|verschil|waarom|analyseer|verklaar|redeneer|bewijs|leg uit waarom|welke conclusie|wat is de oorzaak|wat klopt er niet|wat is de fout|wat is zijn fout|welke stap mist|wat doet.{0,15}(?:fout|verkeerd)|corrigeer|beoordeel|reken\s*terug|terugrekenen|oorspronkelijke\s+(?:prijs|bedrag|waarde)|vóór\s+de\s+(?:korting|verhoging|actie|afprijzing)/i

/** Application-level indicators: calculation, formula use, step execution */
const APPLICATION_PATTERNS =
  /\d+\s*[×x\*÷\/\+\-]\s*\d+|=\s*\d|formule|bereken|stap|eerst.*dan|vervolgens|oplossen|uitrekenen|toepassen/i

/** Comprehension-level indicators: meaning, explanation, interpretation */
const COMPREHENSION_PATTERNS =
  /betekent|want|omdat|uitleg|verklaring|bedoelt|beschrijf|wat is het verschil|hoe werkt|wat begrijp/i

/** Recall-level indicators: name, list, define, identify */
const RECALL_PATTERNS =
  /wat is|welke|noem|geef|schrijf op|hoeveel|wanneer|wie|waar|definitie|omschrijf/i

// ─── Tier mapping: CognitiveTier (signals) ↔ CognitiveLevel (Bloom) ──────────

/**
 * The cognitive-signals SSOT uses 4 tiers (recall/application/strategic/extended)
 * aligned with DOK. Bloom uses 4 (recall/comprehension/application/analysis).
 * The mapping is deliberate and documented:
 *   - 'recall'      → 'recall'      (direct match)
 *   - 'application' → 'application' (direct match)
 *   - 'strategic'   → 'analysis'    (DOK3 strategic thinking = Bloom analysis)
 *   - 'extended'    → 'analysis'    (DOK4 extended thinking ceilings at Bloom analysis)
 *
 * 'comprehension' is a Bloom-only level — the SSOT does not assert it as a
 * structural floor/ceiling because no question TYPE structurally implies it.
 * Comprehension can only be reached via regex tiebreaker WITHIN bounds.
 */
function signalTierToCognitiveLevel(
  tier: CognitiveTier,
): Exclude<CognitiveLevel, 'unknown'> {
  switch (tier) {
    case 'recall':
      return 'recall'
    case 'application':
      return 'application'
    case 'strategic':
      return 'analysis'
    case 'extended':
      return 'analysis'
  }
}

/** Inverse: a Bloom level → its position in the signal tier hierarchy. */
const COGNITIVE_LEVEL_TO_SIGNAL_TIER: Record<
  Exclude<CognitiveLevel, 'unknown'>,
  CognitiveTier
> = {
  recall: 'recall',
  comprehension: 'application',
  // comprehension sits between recall and application; we clamp it as application
  // for structural-bounds checking (it is never a structural floor/ceiling itself).
  application: 'application',
  analysis: 'strategic',
}

// ─── Main detection function ─────────────────────────────────────────────────

/** Bloom-levels the explicit author-field may use; mapped to CognitiveLevel. */
const EXPLICIT_LEVELS: ReadonlySet<CognitiveLevel> = new Set([
  'recall',
  'comprehension',
  'application',
  'analysis',
])

/**
 * Rich detection result with confidence + signal trail (FG5 §3 —
 * Confidence-Weighted Aggregation). Downstream consumers (admin dashboard,
 * gate evaluation) can filter on confidence < threshold for human review.
 *
 * Anti-bandaid principle #5: heuristics return CONFIDENCE; consumers choose
 * the threshold. The heuristic never pretends to be truth.
 */
export interface CognitiveLevelDetail {
  level: CognitiveLevel
  /** 1.0 = author-asserted, 0.8 = structural, 0.4 = regex, 0 = unknown */
  confidence: number
  /** Ordered list of signals consulted, newest first. For observability. */
  signals: string[]
}

/**
 * Detects the cognitive level of a question — STRUCTURE-FIRST.
 *
 * Returns the legacy CognitiveLevel union for backwards compatibility.
 * Prefer `detectCognitiveLevelDetailed` for new code that needs confidence.
 *
 * ⚠️ USE AS A DIVERSITY HINT, NOT A GATE (OOS-3,
 * GOVERNANCE_SYSTEM_HARDENING_AUDIT_2026-06 §10). This Bloom inference is a
 * soft signal: `questionSelector` uses it only to *prefer* a different
 * cognitive level when recent picks were monotone (`questionSelector.ts:267`),
 * never to block. It deliberately does NOT clamp `multiple_choice` to DOK2 the
 * way `dokAutoTagger`'s sidecar `cognitiveCeiling` does — the two ceilings
 * serve different goals (Bloom-inference window here vs author-nudge ceiling
 * for seed auto-fill there) and are intentionally NOT reconciled. Do not reuse
 * this as a hard floor/ceiling or as a DOK source — for the auto-tag ceiling
 * read `dokAutoTagger.estimateDok`; for the structural bounds read
 * `cognitiveSignals.getCognitiveBoundsForType`.
 */
export function detectCognitiveLevel(question: Question): CognitiveLevel {
  return detectCognitiveLevelDetailed(question).level
}

/**
 * Detect cognitive level with confidence + signal trail.
 *
 * Precedence (FG5 structure-first):
 *   1. Explicit author annotation                       confidence 1.0
 *   2. Structural floor from cognitiveSignals SSOT      confidence 0.8 baseline
 *      - raised to application when solutionSteps ≥ 2
 *      - clamped within [floor, ceiling] via structural bounds
 *   3. Regex cascade — operates WITHIN [floor, ceiling]  confidence 0.4
 *   4. unknown                                            confidence 0.0
 */
export function detectCognitiveLevelDetailed(
  question: Question,
): CognitiveLevelDetail {
  const signals: string[] = []

  // 1. Author annotation — highest authority.
  const explicit = question.pedagogy?.cognitiveLevel as
    | CognitiveLevel
    | undefined
  if (explicit && EXPLICIT_LEVELS.has(explicit)) {
    signals.push(`author annotation = ${explicit}`)
    return { level: explicit, confidence: 1.0, signals }
  }

  // 2. Structural bounds from the cognitive-signals SSOT.
  //    Returns floor/ceiling per question type — same facts dokAutoTagger uses.
  const bounds = question.questionType
    ? getCognitiveBoundsForType(question.questionType)
    : undefined
  if (bounds) {
    signals.push(
      `structural bounds [${bounds.floor.tier}..${bounds.ceiling.tier}] for type "${question.questionType}"`,
    )
  }

  // 2a. solutionSteps length signal — multi-step solutions imply application+.
  //     ≥ 2 distinct steps means the learner must execute a procedure, not
  //     just recall. We do NOT push directly to analysis here; the regex
  //     cascade or author annotation does that.
  const solutionStepCount = question.feedback?.solutionSteps?.length ?? 0
  const stepsImplyApplication = solutionStepCount >= 2

  // 3. Regex cascade — produces a CANDIDATE level. This is now a TIEBREAKER
  //    that runs WITHIN the structural [floor, ceiling] window, not a
  //    decisive ranker on its own.
  const questionText = question.question ?? ''
  const explanationText = question.feedback?.explanationText ?? ''
  const combined = `${questionText} ${explanationText}`.trim()

  let regexCandidate: CognitiveLevel = 'unknown'
  if (combined) {
    if (ANALYSIS_PATTERNS.test(combined)) regexCandidate = 'analysis'
    else if (APPLICATION_PATTERNS.test(combined)) regexCandidate = 'application'
    else if (COMPREHENSION_PATTERNS.test(combined))
      regexCandidate = 'comprehension'
    else if (RECALL_PATTERNS.test(combined)) regexCandidate = 'recall'
  }
  if (regexCandidate !== 'unknown') {
    signals.push(`regex candidate = ${regexCandidate}`)
  }

  // ─── Resolve: combine structural + regex + solutionSteps ────────────────

  // Build the candidate. Start from the regex result; if regex was silent
  // fall back to the structural floor.
  let candidate: CognitiveLevel = regexCandidate
  let confidence = 0.4

  if (candidate === 'unknown' && bounds) {
    candidate = signalTierToCognitiveLevel(bounds.floor.tier)
    confidence = 0.8
    signals.push(`fallback to structural floor = ${candidate}`)
  }

  // solutionSteps lifts recall to application (never demotes a higher candidate).
  if (
    stepsImplyApplication &&
    (candidate === 'recall' || candidate === 'comprehension')
  ) {
    candidate = 'application'
    confidence = Math.max(confidence, 0.6)
    signals.push(
      `solutionSteps.length=${solutionStepCount} lifts to application`,
    )
  }

  // Clamp to structural [floor, ceiling] when we have bounds.
  if (candidate !== 'unknown' && bounds) {
    const candidateTier = COGNITIVE_LEVEL_TO_SIGNAL_TIER[candidate]
    const clamp = clampToCognitiveBounds(candidateTier, bounds)
    if (clamp.clamped) {
      const clamped = signalTierToCognitiveLevel(clamp.tier)
      signals.push(`clamped: ${candidate} → ${clamped} (${clamp.reason})`)
      candidate = clamped
      // Clamping is a structural correction — confidence rises since the
      // result is now bounded by what the question type structurally allows.
      confidence = Math.max(confidence, 0.7)
    }
  }

  if (candidate === 'unknown') {
    signals.push('all signals silent — unknown')
    return { level: 'unknown', confidence: 0, signals }
  }

  return { level: candidate, confidence, signals }
}

// NOTE: `cognitiveLevelFromDok(dok)` was removed in RC-3 (G-14, audit 2026)
// as a dead export. The bridge mapping (DOK→CognitiveLevel) is trivial; if a
// future admin-display consumer needs it, recreate from the lens definitions
// in docs/architecture/COGNITIVE_SIGNALS.md (DOK ↔ CognitiveLevel section).
//
// NOTE: stab-ronde 2 (mei 2026) — `cognitiveLevelLabel`, `cognitiveLevelColor`,
// en `computeCognitiveLevelDistribution` zijn verwijderd: nul consumers in src/.
// Voor admin-display: hercreëer een trivial mapping in de UI-laag zelf.
